import {
  enumInventoryTagStatus,
  InventoryTagSortOption,
  InventoryTagStatus,
  TagType,
} from '@rematter/frontend-lib-api-client';

import {
  InventoryTagRecord,
  InventoryTagSummaryStats,
} from '../types/inventoryTag';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const inventoryTagsQueryAtom = createQueryAtomFactory(
  'inventory.inventoryTags',
  (params: {
    searchTerm?: string;
    facilityId?: string;
    tagType?: TagType[];
    status?: InventoryTagStatus[];
    itemId?: string;
    limit?: number;
    offset?: number;
    sorting?: InventoryTagSortOption[];
  }) => ({
    inventoryTags: {
      __args: {
        filter: {
          searchTerm: params.searchTerm,
          facilityIds: params.facilityId ? [params.facilityId] : undefined,
          tagTypes: params.tagType,
          statuses: params.status,
          itemIds: params.itemId ? [params.itemId] : undefined,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 25,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        tagNumber: true,
        tagType: true,
        generatedAt: true,
        status: true,
        grossQuantity: true,
        tare: true,
        netQuantity: true,
        item: {
          id: true,
          name: true,
          skuNumber: true,
        },
        facilityId: true,
        lotId: true,
        lot: {
          lotNumber: true,
        },
        isRevised: true,
        quantityCaptureMethod: true,
        tareCaptureMethod: true,
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (
      data,
    ): {
      tags: InventoryTagRecord[];
      stats: InventoryTagSummaryStats;
      totalCount: number;
    } => {
      if (!data || !data.inventoryTags || !data.inventoryTags.entries) {
        return {
          tags: [],
          stats: {
            totalTags: 0,
            activeTags: 0,
            draftTags: 0,
            inInventoryTags: 0,
          },
          totalCount: 0,
        };
      }

      const tags = data.inventoryTags.entries.map((tag) => ({
        id: tag.id,
        tagNumber: tag.tagNumber,
        tagType: tag.tagType,
        generatedAt: tag.generatedAt,
        status: tag.status,
        grossQuantity: Number(tag.grossQuantity),
        tare: Number(tag.tare),
        netQuantity: Number(tag.netQuantity),
        itemId: tag.item?.id || '',
        itemName: tag.item?.name || '',
        itemSku: tag.item?.skuNumber || null,
        facilityId: tag.facilityId,
        facilityName: '',
        lotId: tag.lotId || undefined,
        lotNumber: tag.lot?.lotNumber || undefined,
        isRevised: tag.isRevised,
        quantityCaptureMethod: tag.quantityCaptureMethod,
        tareCaptureMethod: tag.tareCaptureMethod,
        createdAt: tag.timestamps?.createdAt || '',
        updatedAt: tag.timestamps?.updatedAt || '',
      }));

      const activeTags = tags.filter(
        (tag) => tag.status === enumInventoryTagStatus.Active,
      ).length;
      const draftTags = tags.filter(
        (tag) => tag.status === enumInventoryTagStatus.Draft,
      ).length;
      const inInventoryTags = tags.filter(
        (tag) => tag.status === enumInventoryTagStatus.InInventory,
      ).length;

      return {
        tags,
        stats: {
          totalTags: data.inventoryTags.page?.total || 0,
          activeTags,
          draftTags,
          inInventoryTags,
        },
        totalCount: data.inventoryTags.page?.total || 0,
      };
    },
  },
);
